import { useEffect } from 'react'
import { useLocation } from '@tanstack/react-router'
import PAGES from '@/meta'

/**
 * Hook to set dynamic page titles based on the current route
 *
 * @param title - Optional custom title. If provided, this will override the default route-based title.
 *                Can be a string or a function that returns a string for dynamic titles.
 *                For dynamic titles, pass the computed string directly rather than a function.
 *
 * @example
 * // Static title from PAGES
 * usePageTitle()
 *
 * // Custom static title
 * usePageTitle('Custom Page Title')
 *
 * // Dynamic title - compute the string directly
 * const { id } = Route.useParams()
 * const CITY_NAME = import.meta.env.VITE_CITY_NAME ?? ''
 * usePageTitle(`Drive Details - ${id} | ${CITY_NAME}`)
 */
export function usePageTitle(title?: string) {
  const location = useLocation()

  useEffect(() => {
    let pageTitle: string

    if (title) {
      // Use provided title
      pageTitle = title
    } else {
      // Map route pathname to PAGES entry
      const routeKey = getRouteKey(location.pathname)
      pageTitle = PAGES[routeKey]?.title || PAGES.index.title
    }

    document.title = pageTitle
  }, [location.pathname, title])
}

/**
 * Maps route pathname to PAGES object key
 */
function getRouteKey(pathname: string): keyof typeof PAGES {
  // Remove leading slash and split
  const segments = pathname.split('/').filter(Boolean)

  if (segments.length === 0) return 'index'

  const firstSegment = segments[0]

  // Map route segments to PAGES keys
  const routeMap: Record<string, keyof typeof PAGES> = {
    '': 'index',
    scheduling: 'scheduling',
    'change-password': 'changePassword',
    login: 'login',
    drivers: 'drivers',
    vehicles: 'vehicles',
    'vehicle-versions': 'vehicleVersions',
    routes: 'routes',
    'drive-statistics': 'driveStatistics',
    'drive-details': 'driveDetails',
    'hotline-calls': 'hotlineCalls',
    shifts: 'shifts',
    hardware: 'hardware',
    'car-navigation': 'carNavigation',
    'unexpected-events': 'unexpectedEvents',
    billing: 'billing',
  }

  return routeMap[firstSegment] || 'index'
}
